import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, ShieldCheck, Send, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Switch } from "../ui/switch";
import api from "../../lib/api";

/**
 * Card de configuración del email mensual de scorecard de proveedores.
 *
 * El owner puede:
 * - Activar/desactivar el envío automático mensual (default OFF).
 * - Sobreescribir el email destinatario (default: email del owner).
 * - Disparar un envío manual inmediato para previsualizarlo.
 *
 * Endpoints:
 *   GET  /restaurants/{rid}/suppliers/scorecard-email/config
 *   PUT  /restaurants/{rid}/suppliers/scorecard-email/config
 *   POST /restaurants/{rid}/suppliers/scorecard-email/send-now
 */
export default function ScorecardEmailConfigCard({ restaurantId }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [emailDraft, setEmailDraft] = useState("");

  const load = async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const cfg = await api.request(
        `/restaurants/${restaurantId}/suppliers/scorecard-email/config`,
      );
      setConfig(cfg);
      setEmailDraft(cfg.email_to || "");
    } catch {
      toast.error("Error cargando configuración");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [restaurantId]);  // eslint-disable-line

  const persist = async (patch) => {
    setSaving(true);
    try {
      const updated = await api.request(
        `/restaurants/${restaurantId}/suppliers/scorecard-email/config`,
        { method: "PUT", body: JSON.stringify(patch) },
      );
      setConfig(updated);
      toast.success("Guardado");
    } catch (e) {
      toast.error(e?.message?.includes("inválido") ? "Email inválido" : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const saveEmailOverride = async () => {
    const v = (emailDraft || "").trim();
    if (v && !v.includes("@")) {
      toast.error("Email inválido");
      return;
    }
    await persist({ email_to: v });
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const res = await api.request(
        `/restaurants/${restaurantId}/suppliers/scorecard-email/send-now?days=30`,
        { method: "POST" },
      );
      if (res.sent) {
        toast.success(`Email enviado a ${res.to}`);
        await load();
      } else {
        toast.error(`No se envió: ${res.skipped_reason}`);
      }
    } catch (e) {
      toast.error(e?.message || "Error al enviar");
    } finally {
      setSending(false);
    }
  };

  if (loading || !config) {
    return (
      <div className="border rounded-xl p-6 flex items-center gap-3 text-muted-foreground">
        <Loader2 className="w-4 h-4 animate-spin" /> Cargando configuración…
      </div>
    );
  }

  const enabled = config.enabled === true;
  const last = config.last_run;

  return (
    <div className="border rounded-xl p-6 space-y-5" data-testid="scorecard-email-config-card">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className="w-10 h-10 rounded-lg bg-indigo-600 text-white flex items-center justify-center flex-shrink-0">
          <ShieldCheck className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base">Email mensual de scorecard</h3>
          <p className="text-sm text-muted-foreground">
            Cada día 1 del mes, recibe un resumen con los TOP 3 mejores y peores proveedores
            (basado en incidencias y completitud) + ahorro potencial estimado.
          </p>
        </div>
        <Switch
          checked={enabled}
          onCheckedChange={(v) => persist({ enabled: v })}
          disabled={saving}
          data-testid="scorecard-email-enabled-switch"
        />
      </div>

      {/* Email override */}
      {enabled && (
        <div className="space-y-2">
          <Label htmlFor="scorecard-email-to" className="text-sm font-medium flex items-center gap-1.5">
            <Mail className="w-3.5 h-3.5" /> Destinatario
          </Label>
          <p className="text-xs text-muted-foreground">
            Por defecto se envía al email del owner. Puedes sobreescribir aquí (p.ej., el del jefe de compras).
          </p>
          <div className="flex items-center gap-2">
            <Input
              id="scorecard-email-to"
              type="email"
              placeholder="compras@mirestaurante.com"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              className="flex-1"
              data-testid="scorecard-email-to-input"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={saveEmailOverride}
              disabled={saving || emailDraft === (config.email_to || "")}
              data-testid="scorecard-email-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Guardar"}
            </Button>
          </div>
        </div>
      )}

      {/* Manual send */}
      {enabled && (
        <div className="border-t pt-4">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium">Enviar ahora (preview)</p>
              <p className="text-xs text-muted-foreground">
                Dispara un envío inmediato con los datos actuales (últimos 30 días).
              </p>
            </div>
            <Button
              size="sm"
              onClick={sendNow}
              disabled={sending}
              className="bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0"
              data-testid="scorecard-email-send-now-btn"
            >
              {sending ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" />
              ) : (
                <Send className="w-3.5 h-3.5 mr-1.5" />
              )}
              Enviar ahora
            </Button>
          </div>

          {last && (
            <div
              className="mt-3 text-xs bg-muted/50 rounded-lg px-3 py-2 flex items-start gap-2"
              data-testid="scorecard-email-last-run"
            >
              {last.status === "sent" ? (
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-700 mt-0.5 flex-shrink-0" />
              ) : (
                <Mail className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
              )}
              <div className="min-w-0 flex-1">
                <p>
                  Último envío:{" "}
                  <strong>
                    {last.status === "sent" ? "OK" : last.status} · {last.period}
                  </strong>
                  {last.to && <span> → {last.to}</span>}
                </p>
                {last.estimated_savings > 0 && (
                  <p className="text-emerald-700">
                    Ahorro estimado reportado: {last.estimated_savings.toLocaleString("es-ES", {
                      style: "currency",
                      currency: "EUR",
                      maximumFractionDigits: 0,
                    })}
                  </p>
                )}
                {last.reason && (
                  <p className="text-muted-foreground italic truncate">
                    Motivo: {last.reason}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Info footer */}
      {enabled && (
        <div className="text-xs text-muted-foreground border-t pt-4">
          <strong>¿Cuándo se envía?</strong> Cada día 1 del mes a las 07:00 UTC. Idempotente:
          1 envío por restaurante por mes (auto). El "Enviar ahora" no cuenta para el dedupe.
        </div>
      )}
    </div>
  );
}
